import { Box, Grid, Menu, Slider, Typography } from "@mui/material"
import * as d3 from "d3"
import * as d3hexbin from "d3-hexbin"
import React, { useEffect, useMemo, useRef, useState } from "react"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import "./NodeToNodeDensity.css"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { useLocalStorage } from "usehooks-ts"
import { NodeResult, NodeResults } from "@custom-types/modelRun"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { generateChartLabel } from "@utils/chartLabelUtils"

type NodeToNodeDensityProps = {
  datasets: NodeResults
  formats: Map<string, string>
  selectedTimesteps: (number | string)[]
  width?: number
  height?: number
  margin?: {
    top: number
    right: number
    bottom: number
    left: number
  }
  version?: string
  runId: string
}

export const NodeToNodeDensity = ({
  datasets,
  formats,
  selectedTimesteps,
  width = 800,
  height = 400,
  margin = { top: 16, right: 16, bottom: 36, left: 88 },
  version,
  runId,
}: NodeToNodeDensityProps) => {
  const dataSetsArray = Object.values(datasets)
  const [radiusValue, setRadiusValue] = useLocalStorage(`nodeToNodeDensityValue`, 20)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const svgRef = useRef<SVGSVGElement | null>(null)
  const svgRefArray = useRef<Record<string, SVGSVGElement>>({})
  const chartGridRef = useRef<HTMLDivElement>(null)

  const title = useMemo(() => {
    if (dataSetsArray[0]?.[0] && dataSetsArray[1]?.[0]) {
      const label1 = generateChartLabel({
        nodeName: dataSetsArray[0][0].node_key,
        nodeUnit: dataSetsArray[0][0].unit_format,
        numberOfRuns: 1,
      })
      const label2 = generateChartLabel({
        nodeName: dataSetsArray[1][0].node_key,
        nodeUnit: dataSetsArray[1][0].unit_format,
        numberOfRuns: 1,
      })
      return `${label1} - ${label2}`
    }

    return ""
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [datasets])

  function createData(dataset1: NodeResult[], dataset2: NodeResult[]) {
    const inputForHexbinFun: { x: number; y: number }[] = []

    dataset1.forEach((item) => {
      if (selectedTimesteps.includes(item.timestep)) {
        const secondNodeDataset = dataset2.find((x) => x.timestep === item.timestep)
        if (secondNodeDataset) {
          item.data.forEach((dataItem: number, index: number) =>
            inputForHexbinFun.push({
              x: dataItem,
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              y: secondNodeDataset.data[index],
            }),
          )
        }
      }
    })

    return inputForHexbinFun
  }

  const flattenData = useMemo(() => {
    let inputForHexbinFun: { x: number; y: number }[] = []

    if (dataSetsArray.length == 2) {
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      inputForHexbinFun = createData(dataSetsArray[0], dataSetsArray[1])
    }

    return inputForHexbinFun
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [datasets, selectedTimesteps])

  const flattenTwoDimentionalData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    const inputForHexbinFun: any[][] = []

    dataSetsArray.forEach((itemX, indexX) => {
      inputForHexbinFun.push([])
      dataSetsArray.forEach((itemY, indexY) => {
        if (indexX === indexY) {
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          inputForHexbinFun[indexX].push({})
        } else {
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          const chartDataset = createData(dataSetsArray[indexX], dataSetsArray[indexY])
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          inputForHexbinFun[indexX].push(chartDataset)
        }
      })
    })

    return inputForHexbinFun
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [datasets, selectedTimesteps])

  function createSvg(
    width: number,
    height: number,
    flattenData: { x: number; y: number }[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    svgRef: any,
    labelX?: string,
    labelY?: string,
    nodeKeyX?: string,
    nodeKeyY?: string,
  ) {
    const adjustedWidth = width - margin.left - margin.right
    const adjustedHeight = height - margin.top - margin.bottom
    const svg = d3.select<SVGSVGElement, unknown>(svgRef.current || svgRef)

    svg.selectAll("*").remove()
    // Add X axis
    const xScale = d3
      .scaleLinear()
      .domain([
        d3.min(flattenData, (d: { x: number; y: number }) => d.x) || 0,
        d3.max(flattenData, (d: { x: number; y: number }) => d.x) || 0,
      ])
      .range([0, adjustedWidth])
    const yScale = d3
      .scaleLinear()
      .domain([
        d3.min(flattenData, (d: { x: number; y: number }) => d.y) || 0,
        d3.max(flattenData, (d: { x: number; y: number }) => d.y) || 0,
      ])
      .range([adjustedHeight, 0])
    const hexbinGenerator = d3hexbin
      .hexbin<{ x: number; y: number }>()
      .x((d: { x: number; y: number }) => xScale(d.x))
      .y((d: { x: number; y: number }) => yScale(d.y))
      .radius(radiusValue)
      .extent([
        [0, 0],
        [adjustedWidth, adjustedHeight],
      ])

    // @ts-ignore
    const bins = hexbinGenerator(flattenData)
    const color = d3
      .scaleSequential(d3.interpolateBlues)
      .domain([0, d3.max(bins, (d) => d.length) || 0])

    const legendWidth = 300,
      lengendHeight = 10,
      legendPosition = {
        x: width - margin.right - legendWidth,
        y: margin.top / 2 + lengendHeight,
      }

    const defs = svg.append("defs")
    const linearGradient = defs.append("linearGradient").attr("id", "linear-gradient")

    linearGradient
      .selectAll("stop")
      .data(
        // @ts-ignore
        color.range().map((d: string, i: number, arr: string[]) => ({
          offset: `${(i / arr.length) * 100}%`,
          color: d,
        })),
      )
      .enter()
      .append("stop")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
      .attr("offset", (d: any) => d.offset)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
      .attr("stop-color", (d: any) => d.color)

    const plotArea = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`)

    plotArea
      .append("g")
      .selectAll("path")
      .data(bins)
      .enter()
      .append("path")
      .attr("d", hexbinGenerator.hexagon())
      .attr("transform", (d) => `translate(${d.x},${d.y})`)
      .attr("fill", (d) => color(d.length))

    svg
      .append("rect")
      .attr("x", legendPosition.x)
      .attr("y", legendPosition.y)
      .attr("width", legendWidth)
      .attr("height", lengendHeight)
      .style("fill", "url(#linear-gradient)")

    if (labelX) {
      svg
        .append("text")
        .attr("id", "title-y")
        .attr("x", width / 2 + 20)
        .attr("y", height - 5)
        .attr("text-anchor", "middle")
        .attr("fill", "grey")
        .attr("font-size", 12)
        .text(labelX)
    }

    if (labelY) {
      svg
        .append("text")
        .attr("id", "title-y")
        .attr("x", -(height / 2))
        .attr("y", 20)
        .attr("text-anchor", "middle")
        .attr("fill", "grey")
        .attr("font-size", 12)
        .attr("transform", "rotate(-90)")
        .text(labelY)
    }

    svg
      .append("g")
      .attr("transform", `translate(${margin.left},${height - margin.bottom})`)
      .call(
        d3.axisBottom(xScale).tickFormat(function (domainValue: d3.NumberValue) {
          return formatValuePythonic(domainValue, formats.get(nodeKeyX || ""))
        }),
      )
    svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`)
      .call(
        d3.axisLeft(yScale).tickFormat(function (domainValue: d3.NumberValue) {
          return formatValuePythonic(domainValue, formats.get(nodeKeyY || ""))
        }),
      )
  }

  useEffect(() => {
    if (dataSetsArray.length === 2) {
      if (flattenData.length && svgRef?.current && dataSetsArray[0]?.[0] && dataSetsArray[1]?.[0]) {
        const label1 = generateChartLabel({
          nodeName: dataSetsArray[0][0].node_key,
          nodeUnit: dataSetsArray[0][0].unit_format,
          numberOfRuns: 1,
        })
        const label2 = generateChartLabel({
          nodeName: dataSetsArray[1][0].node_key,
          nodeUnit: dataSetsArray[1][0].unit_format,
          numberOfRuns: 1,
        })
        createSvg(
          width,
          height,
          flattenData,
          svgRef,
          label1,
          label2,
          dataSetsArray[0][0].node_key,
          dataSetsArray[1][0].node_key,
        )
      }
    }
    if (dataSetsArray.length > 2) {
      dataSetsArray.forEach((itemX, indexX) => {
        dataSetsArray.forEach((itemY, indexY) => {
          if (
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            flattenTwoDimentionalData[indexX][indexY].length &&
            svgRefArray?.current?.[indexX * 10 + indexY] &&
            dataSetsArray[indexX]?.[0] &&
            dataSetsArray[indexY]?.[0]
          ) {
            const labelX = generateChartLabel({
              nodeName: dataSetsArray[indexX][0].node_key,
              nodeUnit: dataSetsArray[indexX][0].unit_format,
              numberOfRuns: 1,
            })
            const labelY = generateChartLabel({
              nodeName: dataSetsArray[indexY][0].node_key,
              nodeUnit: dataSetsArray[indexY][0].unit_format,
              numberOfRuns: 1,
            })
            createSvg(
              Math.round(width / dataSetsArray.length),
              Math.round(width / dataSetsArray.length / 2),
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              flattenTwoDimentionalData[indexX][indexY],
              svgRefArray?.current?.[indexX * 10 + indexY],
              labelX,
              labelY,
              dataSetsArray[indexX][0].node_key,
              dataSetsArray[indexY][0].node_key,
            )
          }
        })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [datasets, height, width, flattenData, radiusValue])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <Grid container spacing={1} className="node-to-node-grid" alignItems="center">
      <Grid item xs="auto">
        <Box onClick={handleClick} sx={{ height: "48px", lineHeight: "48px" }}>
          {anchorEl ? (
            <KeyboardArrowUpIcon
              sx={{
                verticalAlign: "middle",
                position: "relative",
                bottom: "2px",
              }}
            />
          ) : (
            <KeyboardArrowDownIcon
              sx={{
                verticalAlign: "middle",
                position: "relative",
                bottom: "2px",
              }}
            />
          )}
          {"Chart options"}
        </Box>
        <Menu
          id="menu"
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "menu",
            sx: { width: "250px", padding: "8px" },
          }}
        >
          <Typography>Radius</Typography>
          <Slider
            aria-label="Custom radius size"
            defaultValue={20}
            value={radiusValue}
            step={2}
            valueLabelDisplay="auto"
            min={2}
            max={40}
            onChange={(e, value) => setRadiusValue(value as number)}
          />
        </Menu>
      </Grid>
      <Grid item>
        <DownloadChartImageDialog
          buttonProps={{ sx: { ml: 1 } }}
          chartRef={chartGridRef}
          defaultFileName="Density Chart"
          runReference={runId}
        />
      </Grid>
      <Grid item={true} xs={12} ref={chartGridRef}>
        {selectedTimesteps?.length ? (
          <>
            {dataSetsArray.length == 2 && (
              <>
                <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
                  Density Chart for <b>{title}</b> {version ? `Version ${version}` : ""}
                </Typography>
                <svg ref={svgRef} width={width} height={height}></svg>
              </>
            )}
            {dataSetsArray.length > 2 && (
              <>
                <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
                  Density Chart
                </Typography>
                <Grid container={true}>
                  {dataSetsArray.map((item, indexX) => {
                    return dataSetsArray.map((item, indexY) => {
                      return (
                        <Grid
                          key={indexX * 10 + indexY}
                          item={true}
                          xs={Math.floor(12 / dataSetsArray.length)}
                        >
                          {indexX !== indexY ? (
                            <svg
                              ref={(element) => {
                                if (element) {
                                  svgRefArray.current[indexX * 10 + indexY] = element
                                } else {
                                  delete svgRefArray.current[indexX * 10 + indexY]
                                }
                              }}
                              width={Math.round(width / dataSetsArray.length)}
                              height={Math.round(height / dataSetsArray.length)}
                            ></svg>
                          ) : (
                            <Typography
                              sx={{
                                textAlign: "center",
                                position: "relative",
                                top: "50%",
                              }}
                            >
                              {item[indexX]
                                ? generateChartLabel({
                                    nodeName: item[indexX].node_key,
                                    nodeUnit: item[indexX].unit_format,
                                    numberOfRuns: 1,
                                  })
                                : ""}
                            </Typography>
                          )}
                        </Grid>
                      )
                    })
                  })}
                </Grid>
              </>
            )}
          </>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "300px", width: "calc(100% - 300px)" }}
          >
            <Typography textAlign="center">Please select timestep</Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  )
}
